import React from 'react'
import moment from 'moment'
import {
  getFormattedDateAndRange,
  offsetDate,
  firstLetter,
  getCurrencySign
} from 'common'
import Link from 'next/link'
import { Alert, Badge, Col, Row, Media, ListGroup, ListGroupItem, Label } from 'reactstrap'
import { useRouter } from 'next/router'
import ReactHtmlParser from 'react-html-parser'
import Pagination from './Pagination'
import useTranslation from '../hooks/UseTranslation'
import ListingIcons from './ListingIcons'
import DayIcon from './DayIcon'
import { UserContext } from '../contexts/UserContext'
import Stars from './Stars'
import ParticipantsBadge from './ParticipantsBadge'
import GurusExtra from './GurusExtra'
import DateQuickAccess from './DateQuickAccess'
import { queryTypes, useQueryState } from 'next-usequerystate'

const IncomingListings = (props) => {
  const { t } = useTranslation()
  const [showGuru, setShowGuru] = React.useState(props.showGuru == true)
  const [pageSize, setPageSize] = React.useState(5)
  const [listings, setListings] = React.useState(props.listings)
  const [queryString, setQueryString] = React.useState('')

  const queryStateArgs = {
    scroll: false,
    shallow: true // Don't run getStaticProps / getServerSideProps / getInitialProps
  }

  const [pageShowing, setPageShowing] = useQueryState(
    'page',
    queryTypes.integer.withDefault(0)
  )

  const [selectedDate, setSelectedDate] = React.useState(listings?.length > 0 ? moment(listings[0].datetime_start) : moment())

  const { remainingTickets } = React.useContext(UserContext)

  const router = useRouter()

  React.useEffect(() => {
    if (listings?.length > 0) {
      if (selectedDate) {
        // first listing after selected date
        const findFirst = (listing) => moment(listing.datetime_start).isSameOrAfter(selectedDate)
        const index = listings.findIndex(findFirst)
        if (index >= 0) {
          setPageShowing(Math.floor(index / pageSize), queryStateArgs)
        } else {
          setPageShowing(-1, queryStateArgs)
        }
      } else {
        setPageShowing(0, queryStateArgs)
      }
    } else {
      setPageShowing(0, queryStateArgs)
    }
  }, [selectedDate, listings])

  React.useEffect(() => {
    setListings(props.listings)
    setPageShowing(0, queryStateArgs)
  }, [props.listings])

  React.useEffect(() => {
    if (props.pageSize > 0) {
      setPageSize(props.pageSize)
    }
  }, [props.pageSize])

  React.useEffect(() => {
    if (router.isReady && router.query.code) {
      setQueryString(`?code=${router.query.code}`)
    }
  }, [router.isReady])
  let nPages = 0
  if (listings) {
    const quotient = Math.floor(listings.length / pageSize)
    const remainder = listings.length % pageSize
    nPages = quotient + (remainder > 0 ? 1 : 0)
  }
  const itemStart = pageShowing * pageSize
  const itemEnd = Math.min((pageShowing + 1) * pageSize, listings.length)
  const perDay = {}
  let ownerId = null
  let hasOnlineCredit = false
  let hasPhysicalCredit = false
  let hasAllCredit = false
  listings.slice(itemStart, itemEnd).map((listing) => {
    ownerId = listing.owner.id
    const day = moment(offsetDate(listing.datetime_start)).format('YYYY MM DD')
    if (perDay[day]) {
      perDay[day].push(listing)
    } else {
      perDay[day] = [listing]
    }
  })

  if (ownerId && remainingTickets) {
    const tickets = remainingTickets[ownerId]
    if (tickets) {
      hasOnlineCredit = Boolean(tickets.online)
      hasPhysicalCredit = Boolean(tickets.physical)
      hasAllCredit = Boolean(tickets.all)
    }
  }

  const getPriceWidget = (listing) => {
    const hasCredit =
      hasAllCredit ||
      (listing.is_online && hasOnlineCredit) ||
      (!listing.is_online && hasPhysicalCredit)
    return listing.is_offers_apply_pack && hasCredit ? (
      <div className='text-right'>
        <span
          className='text-muted mr-2'
          style={{ textDecoration: 'line-through' }}
        >
          {/* fixme: currency harcoded */}
          {listing.price}&nbsp;{getCurrencySign('EUR')}
        </span>
        <span className='h4 text-primary'>
          {ReactHtmlParser(t('pack_1_credit'))}
        </span>
      </div>
    ) : (
      <span className='h4 text-primary'>
        {/* fixme: currency harcoded */}
        {listing.price}&nbsp;{getCurrencySign('EUR')}
      </span>
    )
  }

  const mainLg = showGuru ? 10 : 12
  return (
    <div>
      <div className='mb-4'>
        <Label className='form-label'>{t('quick_jump_date')}</Label>
        <DateQuickAccess date={selectedDate} setDate={setSelectedDate} />
      </div>
      {pageShowing < 0 && <Alert color='warning'>
        {t("no_items_founds")}
      </Alert>}
      <div className='mb-3'>
        {Object.keys(perDay)
          .sort()
          .map((key) => {
            const listingsForDay = perDay[key]
            const day = moment(offsetDate(listingsForDay[0].datetime_start))
            listingsForDay.sort((a, b) => {
              // have to return 1 & -1, not true / false
              let order = 0
              if (a.datetime_start === b.datetime_start) {
                order = a.id > b.id ? 1 : -1
              } else {
                order = moment(a.datetime_start).isAfter(
                  moment(b.datetime_start)
                )
                  ? 1
                  : -1
              }
              return order
            })
            return (
              <div className='d-sm-flex p-2' key={key}>
                <div className='mb-3 mb-sm-0'>
                  <DayIcon day={day} />
                </div>
                <div className='flex-grow-1'>
                  <ListGroup className='shadow'>
                    {listingsForDay.map((listing) => (
                      <Link
                        href='/classes/[slug]'
                        key={listing.slug}
                        as={`/classes/${listing.slug}${queryString}`}
                        passHref
                      >
                        <ListGroupItem action tag='a' target='_blank'>
                          <div>
                            <Row>
                              {showGuru && (
                                <Col>
                                  <div>
                                    <Media className='align-items-center d-flex flex-column justify-content-center'>
                                      <Link
                                        href='/users/[slug]'
                                        as={`/users/${listing.owner.slug}`}
                                      >
                                        <a>
                                          <img
                                            src={
                                              listing.owner.avatar ??
                                              '/content/img/avatar/dummy-user.jpg'
                                            }
                                            alt={listing.owner.name_display}
                                            className='avatar avatar-border-white'
                                          />
                                        </a>
                                      </Link>
                                      <Media body>
                                        <p className='mb-0 mt-2 text-center'>
                                          <Link
                                            href='/users/[slug]'
                                            as={`/users/${listing.owner.slug}`}
                                          >
                                            <a className='text-reset'>
                                              <strong>
                                                {listing.owner.name_display}
                                              </strong>
                                              {(listing.owner.n_reviews > 5 ||
                                                listing.owner.rating > 3) && (
                                                  <span className='d-block'>
                                                    <Stars
                                                      stars={listing.owner.rating}
                                                      size='sm'
                                                    />
                                                  </span>
                                                )}
                                            </a>
                                          </Link>
                                        </p>
                                      </Media>
                                    </Media>
                                  </div>
                                </Col>
                              )}
                              <Col lg={mainLg}>
                                <div className='ml-2'>
                                  <div className='d-flex justify-content-between'>
                                    <div className='mb-1 d-sm-flex'>
                                      <div>
                                        <Badge
                                          className='mb-1 mb-sm-0'
                                          color='secondary-light'
                                          style={{ marginRight: '5px' }}
                                        >
                                          {firstLetter(listing.activity.name)}
                                        </Badge>
                                        <ParticipantsBadge listing={listing} />
                                      </div>
                                    </div>
                                    <div>
                                      {props.showPrice &&
                                        getPriceWidget(listing)}
                                    </div>
                                  </div>
                                  <div>
                                    <h6 style={{ display: 'inline-block' }}>
                                      {firstLetter(listing.title)}
                                    </h6>
                                    <div className='mb-2'>
                                      <span className='mr-2'>
                                        <i className='fa-clock far text-muted mr-2' />
                                        {
                                          getFormattedDateAndRange(listing)
                                            .timeRangeFormatted
                                        }
                                      </span>
                                      <span>
                                        <i
                                          className={`fa ${listing.is_online
                                            ? 'fa-globe'
                                            : 'fa-map-marker-alt'
                                            } text-muted mr-2`}
                                        />
                                        {listing.is_online
                                          ? firstLetter(t('online'))
                                          : listing.ppty.location.city}
                                      </span>
                                    </div>
                                  </div>
                                  <div>
                                    <div className='mb-n4'>
                                      <ListingIcons listing={listing} />
                                    </div>
                                    <div className='pt-2'>
                                      <GurusExtra gurus={listing.gurus_extra} />
                                    </div>
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </ListGroupItem>
                      </Link>
                    ))}
                  </ListGroup>
                </div>
              </div>
            )
          })}
      </div>
      {nPages > 0 && (
        <Pagination
          n={nPages}
          selected={pageShowing}
          onPageChange={(page) => {
            props.onPageChange && props.onPageChange()
            setPageShowing(page, queryStateArgs)
            
            if (listings[page * pageSize]) {
              setSelectedDate(moment(listings[page * pageSize].datetime_start))
            }
          }}
        />
      )}
    </div>
  )
}
{
  /* {perDay.map((listing, i) =>
            <Col sm="6" lg="4" className="mb-30px hover-animate" key={i}>

            </Col>
        )} */
}

export default IncomingListings
